import React, { useState, useEffect, useRef } from "react";
import {
  Card,
  Typography,
  Avatar,
  Space,
  Input,
  Button,
  Checkbox,
  Upload,
  Dropdown,
  message,
  Divider,
  Row,
  Col,
  Spin,
  Tooltip,
  Image,
  Modal,
} from "antd";
import {
  UserOutlined,
  RobotOutlined,
  ClockCircleOutlined,
  SendOutlined,
  PaperClipOutlined,
  DeleteOutlined,
  MoreOutlined,
  CloseOutlined,
  PlusCircleOutlined,
  SmileFilled,
  FileImageFilled,
  PictureOutlined,
  LoadingOutlined,
  ReloadOutlined,
  InfoCircleOutlined,
  EditOutlined,
} from "@ant-design/icons";
import { styles } from "../../styles";
import { useAppDispatch } from "../../../../hooks/reduxHooks";
import {
  generateReplyApi,
  fetchChatHistory,
  chatDeleteAPI,
} from "../../../../services/playground.service";
import SelectOption from "../../../../components/common/selectOption";
import SingleChatItem from "./SingleChatItem";
import AdditionalInfoModal from "./AdditionalInfoModal";
import { imageUploadApi } from "../../../../services/knowledgebase.service";
import "./index.css";
import dayjs from "dayjs";
import { useNavigate } from "react-router-dom";
import { logout } from "../../../../store/slices/profile.slice";
import EmptyChat from "./EmptyChat";
import { BulbOutlined } from "@ant-design/icons";

const { Text, Title, Paragraph } = Typography;
const { TextArea } = Input;

const getStoredAdditionalInfoValue = (key) => {
  if (typeof window === "undefined") {
    return "";
  }

  return localStorage.getItem(key) || "";
};

const ADDITIONAL_INFO_STORAGE_KEYS = [
  "playgroundPhoneNumber",
  "playgroundPrimaryProduct",
  "playgroundPrimaryProductLabel",
];

const formatAudioPreviewTime = (value = 0) => {
  if (!Number.isFinite(value)) return "0:00";

  const minutes = Math.floor(value / 60);
  const seconds = Math.floor(value % 60);
  return `${minutes}:${String(seconds).padStart(2, "0")}`;
};

const VoicePreviewPlayer = ({ src, onRemove }) => {
  const audioRef = useRef(null);
  const playerIdRef = useRef(`voice-preview-${Math.random().toString(36).slice(2)}`);
  const [isPlaying, setIsPlaying] = useState(false);
  const [currentTime, setCurrentTime] = useState(0);
  const [duration, setDuration] = useState(0);
  const waveformBars = [12, 18, 9, 24, 16, 30, 11, 22, 14, 27, 10, 20, 28, 13, 24, 17, 31, 12, 21, 15, 26, 10, 19, 23, 14, 29, 11, 20];

  useEffect(() => {
    const audio = audioRef.current;
    if (!audio) return undefined;

    const handleTimeUpdate = () => setCurrentTime(audio.currentTime || 0);
    const handleLoadedMetadata = () => setDuration(audio.duration || 0);
    const handleEnded = () => {
      setIsPlaying(false);
      setCurrentTime(0);
    };

    audio.addEventListener("timeupdate", handleTimeUpdate);
    audio.addEventListener("loadedmetadata", handleLoadedMetadata);
    audio.addEventListener("ended", handleEnded);

    return () => {
      audio.pause();
      audio.removeEventListener("timeupdate", handleTimeUpdate);
      audio.removeEventListener("loadedmetadata", handleLoadedMetadata);
      audio.removeEventListener("ended", handleEnded);
    };
  }, [src]);

  useEffect(() => {
    const handleOtherVoicePlay = (event) => {
      if (event.detail?.playerId === playerIdRef.current) return;

      audioRef.current?.pause();
      setIsPlaying(false);
    };

    window.addEventListener("echo-voice-play", handleOtherVoicePlay);
    return () => window.removeEventListener("echo-voice-play", handleOtherVoicePlay);
  }, []);

  const progress = duration ? (currentTime / duration) * 100 : 0;

  const handleTogglePlay = async () => {
    const audio = audioRef.current;
    if (!audio) return;

    if (isPlaying) {
      audio.pause();
      setIsPlaying(false);
      return;
    }

    try {
      window.dispatchEvent(
        new CustomEvent("echo-voice-play", {
          detail: { playerId: playerIdRef.current },
        })
      );
      await audio.play();
      setIsPlaying(true);
    } catch (error) {
      message.error("Unable to play this voice message.");
    }
  };

  const handleSeek = (event) => {
    const audio = audioRef.current;
    if (!audio) return;

    const bounds = event.currentTarget.getBoundingClientRect();
    const clickRatio = bounds.width ? (event.clientX - bounds.left) / bounds.width : 0;
    const nextTime = Math.max(0, Math.min(1, clickRatio)) * (duration || 0);
    audio.currentTime = nextTime;
    setCurrentTime(nextTime);
  };

  return (
    <div
      style={{
        display: "inline-flex",
        alignItems: "center",
        gap: "8px",
        maxWidth: "100%",
      }}
    >
      <div
        style={{
          width: "270px",
          maxWidth: "calc(100vw - 130px)",
          padding: "9px 11px",
          backgroundColor: "#ffffff",
          border: "1px solid #e5e7eb",
          borderRadius: "999px",
          boxShadow: "0 4px 14px rgba(15, 23, 42, 0.07)",
        }}
      >
        <audio ref={audioRef} src={src} preload="metadata" />
        <div style={{ display: "flex", alignItems: "center", gap: "9px" }}>
          <Button
            type="primary"
            shape="circle"
            onClick={handleTogglePlay}
            style={{
              width: "32px",
              height: "32px",
              minWidth: "32px",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              backgroundColor: "#1677ff",
              boxShadow: "0 4px 10px rgba(22, 119, 255, 0.24)",
            }}
          >
            <iconify-icon
              icon={isPlaying ? "mdi:pause" : "mdi:play"}
              width="18"
              height="18"
              style={{ marginLeft: isPlaying ? 0 : "2px" }}
            />
          </Button>
          <div
            onClick={handleSeek}
            style={{
              flex: 1,
              minWidth: 0,
              height: "34px",
              display: "flex",
              alignItems: "center",
              gap: "3px",
              cursor: duration ? "pointer" : "default",
            }}
          >
            {waveformBars.map((height, index) => {
              const barProgress = ((index + 1) / waveformBars.length) * 100;
              const isFilled = progress >= barProgress;

              return (
                <span
                  key={index}
                  style={{
                    width: "3px",
                    height: `${height}px`,
                    borderRadius: "999px",
                    backgroundColor: isFilled ? "#1677ff" : "#d1d5db",
                    transition: "background-color 0.15s ease",
                  }}
                />
              );
            })}
          </div>
          <div
            style={{
              minWidth: "38px",
              textAlign: "right",
              fontSize: "11px",
              color: "#6b7280",
              fontWeight: 600,
              fontVariantNumeric: "tabular-nums",
            }}
          >
            {formatAudioPreviewTime(isPlaying ? currentTime : duration || currentTime)}
          </div>
        </div>
      </div>
      <Button
        type="text"
        shape="circle"
        icon={<DeleteOutlined style={{ color: "#595959", fontSize: 13 }} />}
        onClick={onRemove}
        style={{
          minWidth: 30,
          width: 30,
          height: 30,
          padding: 0,
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          backgroundColor: "#ffffff",
          border: "1px solid #e5e7eb",
          boxShadow: "0 2px 8px rgba(15, 23, 42, 0.08)",
        }}
      />
    </div>
  );
};

// Date Separator Component
const DateSeparator = ({ date }) => {
  // Format the date appropriately
  const formatDate = (dateString) => {
    const today = dayjs().startOf("day");
    const yesterday = today.subtract(1, "day");
    const messageDate = dayjs(dateString).startOf("day");

    if (messageDate.isSame(today)) {
      return "Today";
    } else if (messageDate.isSame(yesterday)) {
      return "Yesterday";
    } else {
      // For older dates, format as "Jan 1, 2025"
      return messageDate.format("MMM D, YYYY");
    }
  };

  return (
    <div
      style={{
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        margin: "16px 0",
        width: "100%",
      }}
    >
      <div
        style={{
          backgroundColor: "#f0f0f0",
          borderRadius: "16px",
          padding: "4px 12px",
          display: "inline-block",
        }}
      >
        <Text style={{ fontSize: "12px", color: "#666" }}>
          {formatDate(date)}
        </Text>
      </div>
    </div>
  );
};

const ChatWindowSection = ({
  responseData,
  setResponseData,
  setVisible,
  setSpinning,
  setDrawerData,
  isMobile,
}) => {
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  // Loading state
  const [loading, setLoading] = useState(false);
  const [isTyping, setIsTyping] = useState(false);
  const [conversations, setConversations] = useState([]);
  const [userInput, setUserInput] = useState("");
  const [fileList, setFileList] = useState([]);
  const [fileURL, setFileURL] = useState([]);
  const [imagePreview, setImagePreview] = useState([]);
  const [voiceMessages, setVoiceMessages] = useState([]);
  const [isRecordingVoice, setIsRecordingVoice] = useState(false);
  const [recordingSeconds, setRecordingSeconds] = useState(0);
  const [simulateAdClick, setSimulateAdClick] = useState(false);
  const [automaticMessage, setAutomaticMessage] = useState(
    "नमस्ते! Enter your ads message here"
  );
  const [primaryProductLabel, setPrimaryProductLabel] = useState(() =>
    getStoredAdditionalInfoValue("playgroundPrimaryProductLabel")
  );
  const [simulatedAutomaticMessageEnabled, setSimulatedAutomaticMessageEnabled] = useState(true);

  // Additional customer info for ad click simulation
  const [phoneNumber, setPhoneNumber] = useState(() =>
    getStoredAdditionalInfoValue("playgroundPhoneNumber")
  );
  const [primaryProduct, setPrimaryProduct] = useState(() =>
    getStoredAdditionalInfoValue("playgroundPrimaryProduct")
  );
  const [additionalInfoModalVisible, setAdditionalInfoModalVisible] = useState(false);

  // Response page number metaData from response
  const [conversationsMeta, setConversationsMeta] = useState();

  // Reference to chat container for scrolling
  const chatContainerRef = useRef(null);
  const mediaRecorderRef = useRef(null);
  const audioChunksRef = useRef([]);
  const recordingTimerRef = useRef(null);
  const recordingStreamRef = useRef(null);
  const voiceMessagesRef = useRef([]);

  // New states for infinite scrolling
  const [page, setPage] = useState(1);
  const [isLoadingMore, setIsLoadingMore] = useState(false);
  const [hasMore, setHasMore] = useState(true);
  const [shouldScrollToBottom, setShouldScrollToBottom] = useState(true);
  const lastScrollPositionRef = useRef(0);
  const loadingPageRef = useRef(null);
  const pageRef = useRef(1);

  // New state to track if user has intentionally scrolled
  const [hasScrolled, setHasScrolled] = useState(false);
  const [selectedMessageIds, setSelectedMessageIds] = useState([]);
  const [isDeleteDialogVisible, setIsDeleteDialogVisible] = useState(false);
  const [clearCustomerFields, setClearCustomerFields] = useState(false);
  const [deleteScope, setDeleteScope] = useState("all");

  const currentUserId = localStorage.getItem("userId");

  const hasConfiguredAdInfo = !!(phoneNumber || primaryProduct || primaryProductLabel);

  const handleClearConfiguredAdInfo = () => {
    setPhoneNumber("");
    setPrimaryProduct("");
    setPrimaryProductLabel("");

    if (typeof window !== "undefined") {
      ADDITIONAL_INFO_STORAGE_KEYS.forEach((key) => localStorage.removeItem(key));
    }

    message.info("Additional info cleared");
  };

  useEffect(() => {
    setSpinning(true);
    fetchChatHis();
  }, []);

  useEffect(() => {
    voiceMessagesRef.current = voiceMessages;
  }, [voiceMessages]);

  useEffect(() => {
    return () => {
      if (recordingTimerRef.current) {
        clearInterval(recordingTimerRef.current);
      }
      voiceMessagesRef.current.forEach((voice) => URL.revokeObjectURL(voice.previewUrl));
      if (recordingStreamRef.current) {
        recordingStreamRef.current.getTracks().forEach((track) => track.stop());
      }
    };
  }, []);

  // Scroll to bottom whenever conversations change, but only if we're not loading more at the top
  useEffect(() => {
    if (shouldScrollToBottom) {
      // Check if the latest message has media content
      const hasRecentMedia = conversations?.length > 0 &&
        conversations[conversations.length - 1]?.some(msg =>
          msg?.media_ids && msg?.media_ids?.length > 0
        );

      // Add delay if there are images to allow them to load
      if (hasRecentMedia) {
        setTimeout(scrollToBottom, 200);
      } else {
        scrollToBottom();
      }
    }
  }, [conversations, shouldScrollToBottom]);

  const getMessageKey = (message) => {
    if (!message) return null;
    if (message.message_id) return `${message.role}:id:${message.message_id}`;

    return [
      message.role,
      message.created_at,
      message.content,
      Array.isArray(message.media_ids)
        ? message.media_ids.join(",")
        : message.media_ids || "",
    ].join("|");
  };

  const mergeUniqueConversations = (incomingConversations, existingConversations) => {
    const seenMessageKeys = new Set(
      existingConversations
        .flat()
        .map(getMessageKey)
        .filter(Boolean)
    );

    return incomingConversations
      .map((conversation) =>
        conversation.filter((message) => {
          const messageKey = getMessageKey(message);
          if (!messageKey) return true;
          if (seenMessageKeys.has(messageKey)) return false;

          seenMessageKeys.add(messageKey);
          return true;
        })
      )
      .filter((conversation) => conversation.length > 0);
  };

  // Add scroll event listener
  useEffect(() => {
    let scrollTimeout = null;

    const handleScroll = () => {
      const container = chatContainerRef.current;
      if (!container) return;

      // Store the current scroll position
      const currentScrollTop = container.scrollTop;

      // Check if user is scrolling up (away from the bottom)
      if (
        currentScrollTop <
        container.scrollHeight - container.clientHeight - 50
      ) {
        setHasScrolled(true);
      } else {
        // If user is at the bottom of the chat, they're not trying to view older messages
        setHasScrolled(false);
      }

      lastScrollPositionRef.current = currentScrollTop;

      if (scrollTimeout) {
        clearTimeout(scrollTimeout);
      }

      scrollTimeout = setTimeout(() => {
        // Check if user has scrolled to the top (with a small threshold)
        if (currentScrollTop <= 30 && !isLoadingMore && hasMore) {
          loadMoreConversations();
        }
      }, 250);
    };

    const container = chatContainerRef.current;
    if (container) {
      container.addEventListener("scroll", handleScroll);
    }

    return () => {
      if (container) {
        container.removeEventListener("scroll", handleScroll);
      }
      if (scrollTimeout) {
        clearTimeout(scrollTimeout);
      }
    };
  }, [isLoadingMore, hasMore, page]);

  const scrollToBottom = () => {
    if (chatContainerRef.current) {
      chatContainerRef.current.scrollTop =
        chatContainerRef.current.scrollHeight;
    }
  };

  const loadMoreConversations = () => {
    const nextPage = pageRef.current + 1;

    if (isLoadingMore || !hasMore || loadingPageRef.current === nextPage) {
      return;
    }

    loadingPageRef.current = nextPage;

    // Disable auto-scroll when loading more
    setShouldScrollToBottom(false);
    setIsLoadingMore(true);

    // Save the current scroll height to restore position later
    const scrollHeight = chatContainerRef.current?.scrollHeight || 0;

    dispatch(
      fetchChatHistory({
        params: {
          user_id: localStorage.getItem("userId"),
          page_number: nextPage,
        },
        finalCallback: () => {
          setIsLoadingMore(false);
          loadingPageRef.current = null;

          // After new content is loaded, maintain the scroll position
          setTimeout(() => {
            if (chatContainerRef.current) {
              const newScrollHeight = chatContainerRef.current.scrollHeight;
              const heightDifference = newScrollHeight - scrollHeight;
              chatContainerRef.current.scrollTop =
                heightDifference > 0 ? heightDifference : 0;
            }
          }, 50);
        },
        successCallback: (response) => {
          if (!response.responseData || response.responseData.length === 0) {
            setHasMore(false);
            return;
          }

          setConversations((prevConversations) => {
            const uniqueIncomingConversations = mergeUniqueConversations(
              response.responseData,
              prevConversations
            );

            if (uniqueIncomingConversations.length === 0) {
              setHasMore(false);
              return prevConversations;
            }

            return [...uniqueIncomingConversations, ...prevConversations];
          });

          pageRef.current = nextPage;
          setPage(nextPage);

          const totalPages =
            response?.responseMeta?.total_pages ||
            response?.responseMeta?.totalPages;
          if (totalPages && nextPage >= totalPages) {
            setHasMore(false);
          }
        },
        failureCallback: (error) => {
          console.error("Error loading more conversations:", error);
          setHasMore(false);
        },
      })
    );
  };

  const fetchChatHis = () => {
    // Reset pagination data when fetching initial history
    setPage(1);
    pageRef.current = 1;
    loadingPageRef.current = null;
    setHasMore(true);
    setShouldScrollToBottom(true);
    setHasScrolled(false); // Reset scrolled state when fetching chat history

    dispatch(
      fetchChatHistory({
        params: {
          user_id: localStorage.getItem("userId"),
          page_number: 1,
        },
        finalCallback: () => {
          setSpinning(false);
          setLoading(false);
          // Ensure scroll to bottom after loading conversations
          setTimeout(scrollToBottom, 100);
        },
        successCallback: (response) => {
          setConversations(response?.responseData || []);
          setConversationsMeta(response?.responseMeta);
          setSelectedMessageIds([]);

          // Check if there might be more data to load
          if (!response?.responseData || response.responseData.length === 0) {
            setHasMore(false);
          }

          const totalPages =
            response?.responseMeta?.total_pages ||
            response?.responseMeta?.totalPages;
          if (totalPages && totalPages <= 1) {
            setHasMore(false);
          }
        },
        failureCallback: (error) => {
          console.log("Error:", error);
          setHasMore(false);
        },
      })
    );
  };

  const sendMessage = (urls) => {
    const trimmedUserInput = userInput?.trim();
    const shouldInjectAutomatic = simulateAdClick && automaticMessage?.trim();

    if (shouldInjectAutomatic) {
      setSimulateAdClick(false);
    }

    // When sending a message, we always want to scroll to bottom
    setShouldScrollToBottom(true);
    setHasScrolled(false); // Reset scroll state when sending a new message

    const outgoingChatData = [];

    if (shouldInjectAutomatic) {
      outgoingChatData.push({
        data: {
          sender: "Automatic",
          content: automaticMessage.trim(),
        },
        role: "automatic",
      });
    }

    outgoingChatData.push({
      data: {
        content: trimmedUserInput,
        media_ids: urls?.length > 0 ? urls : [],
      },
      role: "user",
    });

    const data = {
      chat_data: outgoingChatData,
      chat_data_format: "role_data",
      media_ids: ["string"],
      media_values: "string",
      message: "string",
      primary_product: primaryProduct || "string",
      primary_product_code: "string",
      tags: ["Facebook"],
      user_id: localStorage.getItem("userId"),
      user_name: localStorage.getItem("username"),
      mode: "elaborated",
      phone_number: phoneNumber || null,
      simulate_ad_click: simulateAdClick,
      simulated_automatic_message: shouldInjectAutomatic
        ? automaticMessage.trim()
        : null,
    };

    const messageTimestamp = new Date().toISOString();

    // Create new messages to display instantly
    const newUserMessage = {
      role: "user",
      content: trimmedUserInput,
      created_at: messageTimestamp,
      media_ids: urls,
    };
    const automaticConversationMessage = shouldInjectAutomatic
      ? {
        role: "automatic",
        sender: "Automatic",
        content: automaticMessage.trim(),
        created_at: messageTimestamp,
      }
      : null;

    // Update conversations
    setConversations((prev) => {
      const updatedConversations = [...prev];
      if (updatedConversations?.length > 0) {
        if (automaticConversationMessage) {
          updatedConversations[updatedConversations?.length - 1].push(
            automaticConversationMessage
          );
        }
        updatedConversations[updatedConversations?.length - 1].push(
          newUserMessage
        );
      } else {
        updatedConversations.push(
          automaticConversationMessage
            ? [automaticConversationMessage, newUserMessage]
            : [newUserMessage]
        );
      }
      return updatedConversations;
    });

    // Reset input and image preview
    setUserInput("");
    setIsTyping(true);
    setImagePreview([]);

    // Dispatch AI response
    dispatch(
      generateReplyApi({
        data: data,
        finalCallback: () => {
          setLoading(false);
          setIsTyping(false);
          // Consider removing this to prevent double-fetching
          fetchChatHis();
        },
        successCallback: (response) => {
          if (response?.response) {
            setResponseData(response?.response);
            console.log("AI Response:", response?.response);

            // Create AI response
            const aiResponse = {
              role: "assistant",
              content: response?.response?.reply,
              created_at: new Date().toISOString(),
              processingTime: response?.response?.processing_time,
            };

            setConversations((prev) => {
              const updated = [...prev];
              if (updated?.length > 0) {
                updated[updated?.length - 1].push(aiResponse);
              } else {
                updated.push([aiResponse]);
              }
              return updated;
            });
          }
        },
        failureCallback: (error) => {
          setIsTyping(false);
          setLoading(false);
        },
      })
    );
  };

  const handleSendMessage = async () => {
    if (isRecordingVoice) {
      message.warning("Please stop recording before sending.");
      return;
    }

    if (!userInput?.trim() && (!fileList || fileList?.length === 0) && voiceMessages.length === 0) return;

    console.log("imagePreview", fileList);
    setLoading(true);
    console.log("User Input -> ", userInput?.trim());

    if ((fileList && fileList?.length > 0) || voiceMessages.length > 0) {
      const formData = new FormData();
      fileList.forEach((file) => {
        formData.append("files", file.originFileObj);
      });
      voiceMessages.forEach((voice) => {
        formData.append("files", voice.file);
      });

      dispatch(
        imageUploadApi({
          data: formData,
          params: { source: "test" },
          // finalCallback: () => sendMessage([]),
          finalCallback: () => { },
          successCallback: (response) => {
            const urls = response?.data?.map((item) => item?.url ?? []);
            setFileURL(urls);
            sendMessage(urls); // Pass uploaded media URLs, including voice messages
            setFileList([]);
            clearVoiceMessages();
          },
          failureCallback: (error) => {
            console.error("Media Upload Error", error);
            setLoading(false);
          },
        })
      );
    } else {
      sendMessage([]);
    }
  };

  // Helper function to validate if a file is a valid image
  const isValidImageFile = (file) => {
    const validExtensions = ["jpg", "jpeg", "png", "gif", "bmp", "webp"];
    const validMimeTypes = ["image/jpeg", "image/jpg", "image/png", "image/gif", "image/bmp", "image/webp"];

    // Get file extension (handle files without extensions)
    const fileNameParts = file.name.split(".");
    const fileExtension = fileNameParts.length > 1 ? fileNameParts[fileNameParts.length - 1].toLowerCase() : "";

    // Check MIME type as primary validation
    const mimeType = file.originFileObj?.type || file.type;

    // File is valid if either extension or MIME type is valid
    const isValidExtension = fileExtension && validExtensions.includes(fileExtension);
    const isValidMimeType = mimeType && validMimeTypes.includes(mimeType);

    return isValidExtension || isValidMimeType;
  };

  // Async function to validate if a file can be read as an image
  const validateImageFile = (file) => {
    return new Promise((resolve) => {
      // First check basic validation
      if (isValidImageFile(file)) {
        resolve(true);
        return;
      }

      // If basic validation fails, try to read the file as an image
      const img = new Image();
      const reader = new FileReader();

      reader.onload = (e) => {
        img.onload = () => {
          console.log("File validated as image through Image object:", file.name);
          resolve(true);
        };
        img.onerror = () => {
          console.log("File failed Image object validation:", file.name);
          resolve(false);
        };
        img.src = e.target.result;
      };

      reader.onerror = () => {
        console.log("File failed FileReader validation:", file.name);
        resolve(false);
      };

      reader.readAsDataURL(file.originFileObj);
    });
  };

  // Handle file change
  const handleFileChange = async ({ fileList }) => {
    const file = fileList[fileList?.length - 1]; // Get last selected file

    if (file && file.originFileObj) {
      const isValid = await validateImageFile(file);

      if (isValid) {
        console.log("fileList", fileList);
        setFileList(fileList);
        const newPreviews = [];

        fileList.forEach((file) => {
          if (file.originFileObj) {
            const reader = new FileReader();
            reader.onload = (event) => {
              newPreviews.push(event.target.result);

              // Only update state once after all files are processed
              if (newPreviews?.length === fileList?.length) {
                setImagePreview(newPreviews);
              }
            };
            reader.readAsDataURL(file.originFileObj);
          }
        });
      } else {
        message.error(
          "Only image files (jpg, jpeg, png, gif, bmp, webp) are allowed."
        );
        // Filter files using async validation
        const validFiles = [];
        for (const f of fileList) {
          if (await validateImageFile(f)) {
            validFiles.push(f);
          }
        }
        setFileList(validFiles);
      }
    } else {
      message.error("File upload failed. Please try again.");
    }
  };

  // Remove an image preview
  const removeImagePreview = (index) => {
    setFileList((prevFileList) =>
      prevFileList.filter((_, idx) => idx !== index)
    );
    setImagePreview((prevState) => prevState.filter((_, idx) => idx !== index));
  };

  const formatRecordingTime = (seconds) => {
    const minutes = Math.floor(seconds / 60);
    const remainingSeconds = seconds % 60;
    return `${minutes}:${String(remainingSeconds).padStart(2, "0")}`;
  };

  const getAudioExtension = (mimeType = "") => {
    if (mimeType.includes("mp4")) return "m4a";
    if (mimeType.includes("mpeg")) return "mp3";
    if (mimeType.includes("ogg")) return "ogg";
    if (mimeType.includes("wav")) return "wav";
    return "webm";
  };

  const clearVoiceMessages = () => {
    voiceMessages.forEach((voice) => URL.revokeObjectURL(voice.previewUrl));
    setVoiceMessages([]);
    setRecordingSeconds(0);
  };

  const removeVoiceMessage = (id) => {
    setVoiceMessages((prev) => {
      const voiceToRemove = prev.find((voice) => voice.id === id);
      if (voiceToRemove) {
        URL.revokeObjectURL(voiceToRemove.previewUrl);
      }

      return prev.filter((voice) => voice.id !== id);
    });
  };

  const stopRecordingStream = () => {
    if (recordingStreamRef.current) {
      recordingStreamRef.current.getTracks().forEach((track) => track.stop());
      recordingStreamRef.current = null;
    }
  };

  const startVoiceRecording = async () => {
    if (!navigator?.mediaDevices?.getUserMedia || typeof MediaRecorder === "undefined") {
      message.error("Voice recording is not supported in this browser.");
      return;
    }

    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      const recorder = new MediaRecorder(stream);

      recordingStreamRef.current = stream;
      audioChunksRef.current = [];
      mediaRecorderRef.current = recorder;

      recorder.ondataavailable = (event) => {
        if (event.data?.size > 0) {
          audioChunksRef.current.push(event.data);
        }
      };

      recorder.onstop = () => {
        const mimeType = recorder.mimeType || "audio/webm";
        const audioBlob = new Blob(audioChunksRef.current, { type: mimeType });
        const audioFile = new File(
          [audioBlob],
          `voice-message-${Date.now()}.${getAudioExtension(mimeType)}`,
          { type: mimeType }
        );
        const previewUrl = URL.createObjectURL(audioBlob);

        setVoiceMessages((prev) => [
          ...prev,
          {
            id: `${Date.now()}-${prev.length}`,
            file: audioFile,
            previewUrl,
          },
        ]);
        audioChunksRef.current = [];
        stopRecordingStream();
      };

      recorder.start();
      setIsRecordingVoice(true);
      setRecordingSeconds(0);
      recordingTimerRef.current = setInterval(() => {
        setRecordingSeconds((seconds) => seconds + 1);
      }, 1000);
    } catch (error) {
      console.error("Voice recording error", error);
      message.error("Microphone access was blocked or unavailable.");
      stopRecordingStream();
    }
  };

  const stopVoiceRecording = () => {
    if (recordingTimerRef.current) {
      clearInterval(recordingTimerRef.current);
      recordingTimerRef.current = null;
    }
    setIsRecordingVoice(false);

    if (mediaRecorderRef.current?.state === "recording") {
      mediaRecorderRef.current.stop();
    } else {
      stopRecordingStream();
    }
  };

  const handleVoiceButtonClick = () => {
    if (isRecordingVoice) {
      stopVoiceRecording();
      return;
    }

    startVoiceRecording();
  };

  const getSelectionGroupIds = (sortedMessages = [], msgIndex = 0) => {
    const currentMessage = sortedMessages[msgIndex];
    if (!currentMessage?.message_id) return [];

    const ids = [currentMessage.message_id];

    if (currentMessage.role === "user") {
      const nextAssistant = sortedMessages
        .slice(msgIndex + 1)
        .find((item) => item?.role === "assistant" && item?.message_id);
      if (nextAssistant?.message_id) ids.push(nextAssistant.message_id);
    } else if (currentMessage.role === "assistant") {
      for (let index = msgIndex - 1; index >= 0; index -= 1) {
        const previousMessage = sortedMessages[index];
        if (previousMessage?.role === "user" && previousMessage?.message_id) {
          ids.push(previousMessage.message_id);
          break;
        }
      }
    }

    return [...new Set(ids)];
  };

  const handleToggleMessageSelect = (messageIds = []) => {
    if (!Array.isArray(messageIds) || messageIds.length === 0) return;

    setSelectedMessageIds((prev) => {
      const allSelected = messageIds.every((id) => prev.includes(id));
      if (allSelected) {
        return prev.filter((id) => !messageIds.includes(id));
      }
      const selectedSet = new Set(prev);
      messageIds.forEach((id) => selectedSet.add(id));
      return Array.from(selectedSet);
    });
  };

  const getSelectionGroupPosition = (sortedMessages = [], msgIndex = 0, selectionGroupIds = []) => {
    if (!Array.isArray(selectionGroupIds) || selectionGroupIds.length === 0) {
      return "single";
    }

    const groupIndexes = sortedMessages
      .map((item, index) =>
        selectionGroupIds.includes(item?.message_id) ? index : -1
      )
      .filter((index) => index !== -1);

    if (groupIndexes.length <= 1) return "single";

    const firstIndex = Math.min(...groupIndexes);
    const lastIndex = Math.max(...groupIndexes);

    if (msgIndex === firstIndex) return "start";
    if (msgIndex === lastIndex) return "end";
    return "middle";
  };

  const openDeleteDialog = (scope = "all") => {
    if (scope === "selected" && selectedMessageIds.length === 0) {
      message.warning("Please select message(s) first.");
      return;
    }

    setDeleteScope(scope);
    setClearCustomerFields(false);
    setIsDeleteDialogVisible(true);
  };

  const handleDeleteChatHistory = (scope = "all") => {
    const isDeletingSelected = scope === "selected";
    const messageIds = isDeletingSelected ? selectedMessageIds : [];

    if (!currentUserId) {
      message.error("Current user ID not found.");
      return;
    }

    dispatch(
      chatDeleteAPI({
        params: {
          user_id: currentUserId,
          message_id: messageIds,
          clear_customer_fields: clearCustomerFields,
          useDeletePayload: true,
        },
        finalCallback: () => { },
        successCallback: (response) => {
          setIsDeleteDialogVisible(false);
          setSelectedMessageIds([]);
          setHasScrolled(false); // Reset scrolled state when clearing chat history
          message.success(
            isDeletingSelected
              ? "Selected message(s) deleted successfully!"
              : "Chat deleted successfully!"
          );
          fetchChatHis();
        },
        failureCallback: (error) => {
          message.error(error.message);
        },
      })
    );
  };

  // AI Typing indicator component
  const TypingIndicator = () => (
    <div style={{ marginBottom: "16px" }}>
      <Space align="start">
        <Avatar
          icon={<RobotOutlined />}
          style={{ backgroundColor: "#1890ff" }}
        />

        <div className="message-loading">
          <div className="bouncing-dots">
            <span></span>
            <span></span>
            <span></span>
          </div>
        </div>
      </Space>
    </div>
  );

  // Render messages with date separators
  const renderMessagesWithDateSeparators = (conversations) => {
    if (!conversations || conversations.length === 0) return [];

    const result = [];
    let currentDate = null;

    conversations.flatMap((conversation, convIndex) => {
      const sortedMessages = [...conversation].sort((a, b) => {
        const timeA = new Date(a.created_at || 0).getTime();
        const timeB = new Date(b.created_at || 0).getTime();
        return timeA - timeB;
      });

      sortedMessages.forEach((message, msgIndex) => {
        const selectionGroupIds = getSelectionGroupIds(sortedMessages, msgIndex);
        const isGroupSelected =
          selectionGroupIds.length > 0 &&
          selectionGroupIds.every((id) => selectedMessageIds.includes(id));
        const selectionGroupPosition = getSelectionGroupPosition(
          sortedMessages,
          msgIndex,
          selectionGroupIds
        );

        // Skip messages without created_at timestamp
        if (!message.created_at) {
          result.push(
            <SingleChatItem
              key={`msg-no-date-${msgIndex}-${convIndex}`}
              message={message}
              convIndex={convIndex}
              msgIndex={msgIndex}
              setVisible={setVisible}
              setResponseData={setResponseData}
              setDrawerData={setDrawerData}
              isSelected={isGroupSelected}
              selectionGroupIds={selectionGroupIds}
              selectionGroupPosition={selectionGroupPosition}
              hasActiveSelection={selectedMessageIds.length > 0}
              onToggleSelect={handleToggleMessageSelect}
            />
          );
          return;
        }

        // Get message date without time (start of day)
        const messageDate = dayjs(message.created_at).startOf("day");

        // If this is a new date, add a separator
        if (!currentDate || !messageDate.isSame(currentDate)) {
          currentDate = messageDate;
          result.push(
            <DateSeparator
              key={`date-${message.created_at}-${convIndex}-${msgIndex}`}
              date={message.created_at}
            />
          );
        }

        // Add the message
        result.push(
          <SingleChatItem
            key={`msg-${message.created_at}-${msgIndex}-${convIndex}`}
            message={message}
            convIndex={convIndex}
            msgIndex={msgIndex}
            setVisible={setVisible}
            setResponseData={setResponseData}
            setDrawerData={setDrawerData}
            isSelected={isGroupSelected}
            selectionGroupIds={selectionGroupIds}
            selectionGroupPosition={selectionGroupPosition}
            hasActiveSelection={selectedMessageIds.length > 0}
            onToggleSelect={handleToggleMessageSelect}
          />
        );
      });
    });

    return result;
  };

  const chatControlSize = isMobile ? 40 : 48;
  const chatControlIconSize = isMobile ? 20 : 24;
  const chatInputPadding = isMobile ? "10px 12px" : "12px 16px";
  const chatInputMinHeight = isMobile ? "40px" : "48px";
  const chatInputLineHeight = isMobile ? "20px" : "24px";
  const chatInputRadius = isMobile ? "20px" : "24px";
  const chatControlGap = isMobile ? "8px" : "10px";
  const hasMessages = conversations?.length > 0;
  const canDeleteChat = selectedMessageIds.length > 0 || hasMessages;

  const mobileHeaderActionItems = [
    ...(selectedMessageIds.length > 0
      ? [
        {
          key: "clear-selection",
          label: "Clear Selection",
        },
      ]
      : []),
    {
      key: "clear-chat",
      label:
        selectedMessageIds.length > 0
          ? `Delete Selected (${selectedMessageIds.length})`
          : "Clear Chat",
      danger: true,
      disabled: !canDeleteChat,
    },
    {
      key: "refresh-chat",
      label: "Refresh",
      disabled: loading,
    },
  ];

  const handleMobileHeaderAction = ({ key }) => {
    if (key === "clear-selection") {
      setSelectedMessageIds([]);
      return;
    }

    if (key === "clear-chat") {
      openDeleteDialog(selectedMessageIds.length > 0 ? "selected" : "all");
      return;
    }

    if (key === "refresh-chat") {
      fetchChatHis();
    }
  };

  return (
    <div
      style={{
        height: "100%",
        width: "100%",
        flex: 1,
        minHeight: 0,
        display: "flex",
        flexDirection: "column",
        overflow: "hidden",
      }}
    >
      <div
        className="playground-chat-header"
        style={{
          display: "flex",
          flexDirection: "row",
          justifyContent: "space-between",
          alignItems: "center",
          padding: "16px",
          borderBottom: "1px solid #f0f0f0",
          flexShrink: 0,
        }}
      >
        <div
          style={{
            display: "flex",
            gap: "4px",
            alignItems: "center",
          }}
        >
          <div style={{ display: "flex", flexDirection: "column", gap: "4px" }}>
            <div style={{ display: "flex", flexDirection: "column" }}>
              <Title level={4} style={{ margin: 0 }}>
                Playground
              </Title>
              <span style={{ fontSize: "11px", color: "#999" }}>
                Test your AI assistant
              </span>
            </div>
          </div>
        </div>

        {isMobile ? (
          <Dropdown
            trigger={["click"]}
            menu={{
              items: [
                ...(selectedMessageIds.length > 0
                  ? [
                    {
                      key: "clear-selection",
                      label: "Clear Selection",
                    },
                  ]
                  : []),
                {
                  key: "clear-chat",
                  label:
                    selectedMessageIds.length > 0
                      ? `Delete Selected (${selectedMessageIds.length})`
                      : "Clear Chat",
                  danger: true,
                  disabled: !canDeleteChat,
                },
                {
                  key: "refresh-chat",
                  label: "Refresh",
                  disabled: loading,
                },
              ],
              onClick: handleMobileHeaderAction,
            }}
            placement="bottomRight"
          >
            <Button
              type="text"
              icon={<MoreOutlined style={{ fontSize: "20px" }} />}
            />
          </Dropdown>
        ) : (
          <div style={{ display: "flex", gap: "8px" }}>
            {selectedMessageIds.length > 0 && (
              <Button
                type="default"
                onClick={() => setSelectedMessageIds([])}
              >
                Clear Selection
              </Button>
            )}
            <Button
              type="default"
              icon={<DeleteOutlined />}
              onClick={() =>
                openDeleteDialog(
                  selectedMessageIds.length > 0 ? "selected" : "all"
                )
              }
              disabled={!canDeleteChat}
              danger
            >
              {selectedMessageIds.length > 0
                ? `Delete Selected (${selectedMessageIds.length})`
                : "Clear Chat"}
            </Button>
            <Button
              type="default"
              icon={<EditOutlined />}
              onClick={() => setAdditionalInfoModalVisible(true)}
            >
              Ad Info
            </Button>
            <Button
              type="default"
              icon={<ReloadOutlined />}
              onClick={() => fetchChatHis()}
              disabled={loading}
            />
          </div>
        )}
      </div>

      {/* Chat Area */}
      <div
        style={{
          display: "flex",
          flexDirection: "column",
          flex: 1,
          minHeight: 0,
          overflow: "hidden",
          position: "relative",
        }}
      >
        <div
          ref={chatContainerRef}
          className="chat-messages"
          style={{
            flex: 1,
            overflowY: "auto",
            overflowX: "hidden",
            padding: isMobile ? "16px 12px" : "24px 16px",
            minHeight: 0,
          }}
        >
          {/* Loading indicator at the top when fetching more conversations */}
          {hasMore && (
            <div
              style={{
                textAlign: "center",
                height: "30px",
                marginBottom: "10px",
              }}
            >
              <Spin
                indicator={<LoadingOutlined spin />}
                spinning={isLoadingMore}
              />
            </div>
          )}

          {/* "No more messages" */}
          {!hasMore && conversations.length > 0 && (
            <div
              style={{
                textAlign: "center",
                color: "#9aa0a6",
                fontSize: "12px",
                fontWeight: 500,
                margin: "10px 0 20px 0",
              }}
            >
              No more messages
            </div>
          )}

          {/* Show empty state when there are no conversations */}
          {!loading && conversations?.length === 0 ? (
            <EmptyChat />
          ) : (
            <>
              {/* Render messages with date separators */}
              {renderMessagesWithDateSeparators(conversations)}

              {/* Show typing indicator when AI is responding */}
              {isTyping && <TypingIndicator />}
            </>
          )}
        </div>

        {/* Image Preview Section */}
        {imagePreview?.length > 0 && (
          <div
            style={{
              display: "flex",
              gap: "16px",
              flexWrap: "wrap",
              padding: "8px 24px",
              backgroundColor: "#f9f9f9",
              borderTop: "1px solid #f0f0f0",
              flexShrink: 0,
              maxHeight: "120px",
              overflowY: "auto",
            }}
          >
            <Image.PreviewGroup>
              {imagePreview?.map((image, index) => (
                <div
                  key={index}
                  style={{
                    position: "relative",
                    display: "flex",
                    flexDirection: "column",
                    alignItems: "center",
                  }}
                >
                  <Image
                    src={image}
                    alt={`Preview ${index}`}
                    style={{
                      width: "80px",
                      height: "80px",
                      borderRadius: "8px",
                      border: "solid 1px #E5E7EB",
                      objectFit: "cover",
                      cursor: "pointer",
                    }}
                    preview={{
                      mask: (
                        <div style={{ fontSize: "12px" }}>Click to view</div>
                      ),
                    }}
                  />
                  <Button
                    type="text"
                    shape="circle"
                    icon={
                      <DeleteOutlined
                        style={{ color: "#333", fontSize: 12 }}
                      />
                    }
                    onClick={() => removeImagePreview(index)}
                    style={{
                      position: "absolute",
                      top: "-8px",
                      right: "-8px",
                      zIndex: 1,
                      minWidth: 24,
                      width: 24,
                      height: 24,
                      padding: 0,
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      backgroundColor: "rgba(255, 255, 255, 0.9)",
                      border: "1px solid rgba(0,0,0,0.1)",
                      borderRadius: "50%",
                      boxShadow: "0 2px 4px rgba(0,0,0,0.1)",
                    }}
                  />
                  <div
                    style={{
                      marginTop: "4px",
                      textAlign: "center",
                      maxWidth: "80px",
                    }}
                  >
                    <div
                      style={{
                        fontSize: "11px",
                        color: "#666",
                        whiteSpace: "nowrap",
                        overflow: "hidden",
                        textOverflow: "ellipsis",
                      }}
                      title={fileList[index]?.name || `Image ${index + 1}`}
                    >
                      {fileList[index]?.name || `Image ${index + 1}`}
                    </div>
                  </div>
                </div>
              ))}
            </Image.PreviewGroup>
          </div>
        )}

        {(isRecordingVoice || voiceMessages.length > 0) && (
          <div
            style={{
              display: "flex",
              alignItems: "center",
              gap: "10px",
              padding: "8px 24px",
              backgroundColor: "#f9f9f9",
              borderTop: "1px solid #f0f0f0",
              flexShrink: 0,
              overflowX: "auto",
            }}
          >
            {voiceMessages.map((voice) => (
              <VoicePreviewPlayer
                key={voice.id}
                src={voice.previewUrl}
                onRemove={() => removeVoiceMessage(voice.id)}
              />
            ))}
            {isRecordingVoice && (
              <div
                style={{
                  display: "flex",
                  alignItems: "center",
                  gap: "8px",
                  padding: "8px 10px",
                  backgroundColor: "#fff1f0",
                  border: "1px solid #ffa39e",
                  borderRadius: "8px",
                  maxWidth: "100%",
                  flexShrink: 0,
                }}
              >
                <iconify-icon
                  icon="mdi:record-circle-outline"
                  width="18"
                  height="18"
                  style={{ color: "#cf1322" }}
                />
                <span style={{ color: "#cf1322", fontSize: "13px", fontWeight: 600 }}>
                  Recording {formatRecordingTime(recordingSeconds)}
                </span>
              </div>
            )}
          </div>
        )}

        {/* Chat Input Section */}
        <div
          className="chat-input playground-chat-input"
          style={{
            padding: isMobile ? "12px" : "16px",
            flexShrink: 0,
          }}
        >
          <div
            style={{
              marginBottom: simulateAdClick ? "10px" : 0,
              padding: simulateAdClick ? "10px 12px" : 0,
              border: simulateAdClick ? "1px dashed #d9d9d9" : "none",
              borderRadius: "10px",
              backgroundColor: simulateAdClick ? "#fafafa" : "transparent",
              display: simulateAdClick ? "block" : "none",
            }}
          >
            {simulateAdClick && (
              <>
                {hasConfiguredAdInfo ? (
                  <div
                    style={{
                      display: "flex",
                      alignItems: "center",
                      gap: "8px",
                      flexWrap: "wrap",
                      width: "fit-content",
                      maxWidth: "100%",
                      padding: "6px 10px",
                      backgroundColor: "#fff7e6",
                      border: "1px solid #ffd591",
                      borderRadius: "8px",
                      color: "#ad6800",
                      fontSize: "12px",
                      fontWeight: 500,
                    }}
                  >
                    <span style={{ display: "inline-flex", alignItems: "center", gap: "4px" }}>
                      <iconify-icon icon="mdi:alert-circle-outline" width="14" height="14" />
                      Additional info is configured.
                    </span>
                    <Button
                      type="link"
                      size="small"
                      icon={<EditOutlined />}
                      onClick={() => setAdditionalInfoModalVisible(true)}
                      style={{ padding: 0, height: "auto", color: "#1677ff", fontWeight: 500 }}
                    >
                      Edit
                    </Button>
                    <Button
                      type="link"
                      size="small"
                      danger
                      onClick={handleClearConfiguredAdInfo}
                      style={{ padding: 0, height: "auto", fontWeight: 500 }}
                    >
                      Clear
                    </Button>
                  </div>
                ) : (
                  <Button
                    type="link"
                    size="small"
                    icon={<EditOutlined />}
                    onClick={() => setAdditionalInfoModalVisible(true)}
                    style={{ paddingLeft: 0 }}
                  >
                    Add customer info
                  </Button>
                )}
              </>
            )}
          </div>

          <div
            style={{
              display: "flex",
              alignItems: "flex-end",
              gap: chatControlGap,
              width: "100%",
            }}
          >
            <Tooltip title="Upload Image">
              <Upload
                fileList={fileList}
                onChange={handleFileChange}
                beforeUpload={() => false}
                showUploadList={false}
                multiple
                accept="image/*"
              >
                <Button
                  type="text"
                  style={{
                    height: chatControlSize,
                    width: chatControlSize,
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    borderRadius: "50%",
                    color: "#666",
                    backgroundColor: "rgba(255, 255, 255, 0.9)",
                    border: "1px solid rgba(0,0,0,0.1)",
                    boxShadow: "0 2px 4px rgba(0,0,0,0.1)",
                  }}
                >
                  <iconify-icon
                    icon="uil:image-upload"
                    width={chatControlIconSize}
                    height={chatControlIconSize}
                  ></iconify-icon>
                </Button>
              </Upload>
            </Tooltip>

            <Tooltip title={isRecordingVoice ? "Stop Recording" : "Record Voice"}>
              <Button
                type="text"
                onClick={handleVoiceButtonClick}
                disabled={loading}
                style={{
                  height: chatControlSize,
                  width: chatControlSize,
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  borderRadius: "50%",
                  color: isRecordingVoice ? "#cf1322" : "#666",
                  backgroundColor: isRecordingVoice ? "#fff1f0" : "rgba(255, 255, 255, 0.9)",
                  border: isRecordingVoice ? "1px solid #ffa39e" : "1px solid rgba(0,0,0,0.1)",
                  boxShadow: "0 2px 4px rgba(0,0,0,0.1)",
                }}
              >
                <iconify-icon
                  icon={isRecordingVoice ? "mdi:stop" : "mdi:microphone-outline"}
                  width={chatControlIconSize}
                  height={chatControlIconSize}
                ></iconify-icon>
              </Button>
            </Tooltip>

            <TextArea
              value={userInput}
              onChange={(e) => setUserInput(e.target.value)}
              placeholder={
                conversations?.length === 0
                  ? simulateAdClick
                    ? "Simulate ad click is ON. Type customer reply to continue..."
                    : "Type your message... Tip: enable 'Simulate ad click' to inject an Automatic message."
                  : "Type your message..."
              }
              autoSize={{ minRows: 1, maxRows: 3 }}
              onPressEnter={(e) => {
                if (!e.shiftKey) {
                  e.preventDefault();
                  handleSendMessage();
                }
              }}
              disabled={loading}
              className="ant-input chat-composer-textarea"
              style={{
                flex: 1,
                border: "none",
                outline: "none",
                background: "#f5f5f5",
                resize: "none",
                padding: chatInputPadding,
                borderRadius: chatInputRadius,
                lineHeight: chatInputLineHeight,
                minHeight: chatInputMinHeight,
              }}
            />

            <Tooltip title="Send Message">
              <Button
                type="primary"
                onClick={handleSendMessage}
                loading={
                  loading
                    ? { icon: <LoadingOutlined style={{ fontSize: 20 }} spin /> }
                    : false
                }
                disabled={!(userInput?.trim() || imagePreview?.length > 0 || voiceMessages.length > 0) || isRecordingVoice}
                shape="circle"
                style={{
                  height: chatControlSize,
                  width: chatControlSize,
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                }}
              >
                {!loading && (
                  <iconify-icon
                    icon="majesticons:send-line"
                    width={chatControlIconSize}
                    height={chatControlIconSize}
                  ></iconify-icon>
                )}
              </Button>
            </Tooltip>
          </div>
        </div>
      </div>
      <AdditionalInfoModal
        modalVisible={additionalInfoModalVisible}
        setModalVisible={setAdditionalInfoModalVisible}
        phoneNumber={phoneNumber}
        setPhoneNumber={setPhoneNumber}
        primaryProduct={primaryProduct}
        setPrimaryProduct={setPrimaryProduct}
        primaryProductLabel={primaryProductLabel}
        setPrimaryProductLabel={setPrimaryProductLabel}
        automaticMessage={automaticMessage}
        setAutomaticMessage={setAutomaticMessage}
        setSimulateAdClick={setSimulateAdClick}
      />
      <Modal
        title={
          deleteScope === "selected"
            ? `Delete ${selectedMessageIds.length} selected message(s)?`
            : "Clear all chats?"
        }
        open={isDeleteDialogVisible}
        onCancel={() => setIsDeleteDialogVisible(false)}
        onOk={() => handleDeleteChatHistory(deleteScope)}
        okText="Delete"
        okButtonProps={{ danger: true }}
      >
        <p style={{ marginBottom: 12 }}>
          {deleteScope === "selected"
            ? "This will remove only the selected messages."
            : "This will remove the full conversation history for this user."}
        </p>
        <Checkbox
          checked={clearCustomerFields}
          onChange={(event) => setClearCustomerFields(event.target.checked)}
        >
          <span style={{ display: "inline-flex", alignItems: "center", gap: "6px" }}>
            <span>Clear the information sent by the customer</span>
            <Tooltip title="Clears customer details in this conversation (e.g., name, phone, etc.).">
              <InfoCircleOutlined
                style={{ color: "#8c8c8c", fontSize: "13px" }}
                onClick={(event) => {
                  event.preventDefault();
                  event.stopPropagation();
                }}
              />
            </Tooltip>
          </span>
        </Checkbox>
      </Modal>
    </div>
  );
};

export default ChatWindowSection;
